import type { Line, LyricData, Word } from '../../vendor/folia-src/types'

interface TimedTextEntry {
  startTime: number
  text: string
}

const TIME_TAG = /\[(\d{1,2}):(\d{1,2})(?:[.:](\d{1,3}))?]/g

const parseTimestamp = (mm: string, ss: string, frac?: string): number => {
  const minutes = Number(mm)
  const seconds = Number(ss)
  let fraction = 0
  if (frac) {
    fraction = frac.length <= 2 ? Number(frac) / 100 : Number(frac) / 1000
  }
  return minutes * 60 + seconds + fraction
}

const parseSimpleTimedTextEntry = (rawLine: string): TimedTextEntry | null => {
  TIME_TAG.lastIndex = 0
  const tags: number[] = []
  let match: RegExpExecArray | null
  let lastIndex = 0
  while ((match = TIME_TAG.exec(rawLine)) !== null) {
    tags.push(parseTimestamp(match[1], match[2], match[3]))
    lastIndex = TIME_TAG.lastIndex
  }
  if (tags.length === 0) return null
  const text = rawLine.slice(lastIndex).trim()
  if (!text) return null
  return { startTime: tags[0]!, text }
}

const buildTimedWords = (text: string, startTime: number, endTime: number): Word[] => {
  const tokens = text.split(/(\s+)/).filter(Boolean)
  if (tokens.length === 0) {
    return [{ text, startTime, endTime }]
  }
  const duration = Math.max(0.01, endTime - startTime)
  const contentTokens = tokens.filter((token) => !/^\s+$/.test(token))
  const step = duration / Math.max(1, contentTokens.length)
  let cursor = startTime
  const words: Word[] = []
  for (const token of tokens) {
    if (/^\s+$/.test(token)) {
      if (words.length > 0) {
        const last = words[words.length - 1]!
        words[words.length - 1] = { ...last, text: `${last.text}${token}` }
      }
      continue
    }
    const wordEnd = Math.min(endTime, cursor + step)
    words.push({ text: token, startTime: cursor, endTime: wordEnd })
    cursor = wordEnd
  }
  return words
}

/** Build Line[] from plain newline-separated lyrics without timestamps. */
export const lyricDataFromPlainText = (
  plain: string,
  meta?: Pick<LyricData, 'title' | 'artist'>,
): LyricData => {
  const rows = String(plain || '')
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean)
  const lines: Line[] = rows.map((text, index) => {
    const startTime = index * 4
    const endTime = startTime + 3.5
    return {
      words: buildTimedWords(text, startTime, endTime),
      startTime,
      endTime,
      fullText: text,
    }
  })
  return { lines, ...meta }
}

export const parseLrc = (lrcString: string, translationString = ''): LyricData => {
  const rawEntries: TimedTextEntry[] = []
  for (const rawLine of lrcString.replace(/^\uFEFF/, '').split(/\r?\n/)) {
    const entry = parseSimpleTimedTextEntry(rawLine)
    if (entry) rawEntries.push(entry)
  }
  rawEntries.sort((a, b) => a.startTime - b.startTime)

  const translationEntries: TimedTextEntry[] = []
  if (translationString.trim()) {
    for (const rawLine of translationString.replace(/^\uFEFF/, '').split(/\r?\n/)) {
      const entry = parseSimpleTimedTextEntry(rawLine)
      if (entry) translationEntries.push(entry)
    }
    translationEntries.sort((a, b) => a.startTime - b.startTime)
  }

  const findTranslation = (startTime: number): string | undefined => {
    let best: string | undefined
    let bestDelta = Number.POSITIVE_INFINITY
    for (const entry of translationEntries) {
      const delta = Math.abs(entry.startTime - startTime)
      if (delta < bestDelta && delta <= 0.35) {
        bestDelta = delta
        best = entry.text
      }
    }
    return best
  }

  const lines: Line[] = []
  for (let index = 0; index < rawEntries.length; index += 1) {
    const current = rawEntries[index]!
    const next = rawEntries[index + 1]
    let duration = next ? next.startTime - current.startTime : 5
    const estimatedReadingTime = current.text.length * 0.5
    if (duration > estimatedReadingTime + 2 && duration > 5) {
      duration = Math.min(duration, estimatedReadingTime + 2)
    }
    const endTime = current.startTime + duration
    lines.push({
      words: buildTimedWords(current.text, current.startTime, endTime),
      startTime: current.startTime,
      endTime,
      fullText: current.text,
      translation: findTranslation(current.startTime),
    })
  }

  return { lines }
}

export const lyricDataFromLines = (
  lines: Line[],
  meta?: Pick<LyricData, 'title' | 'artist' | 'isWordByWord'>,
): LyricData => ({
  lines,
  ...meta,
})
